import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import {
  passwordResetRequestSchema,
  type PasswordResetRequest,
} from "@/contracts";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { TextField } from "@/components/ui/field";
import { useApiClient } from "@/lib/api-context";
import { ApiError } from "@/lib/api-error";
import { useLocale } from "@/i18n/use-locale";
import { AuthBrandLayout } from "./auth-brand-layout";

export function ForgotPasswordPage() {
  const { t } = useTranslation("pages");
  const client = useApiClient();
  const { locale } = useLocale();
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    document.title = `${t("forgotHeading")} · ZonaClara`;
  }, [t]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PasswordResetRequest>({
    resolver: zodResolver(passwordResetRequestSchema),
    defaultValues: { email: "", locale },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);

    try {
      await client.requestPasswordReset({ ...values, locale });
      setSent(true);
    } catch (error) {
      if (error instanceof ApiError && error.code === "RATE_LIMITED") {
        setFormError(t("forgotRateLimited"));
        return;
      }

      setFormError(error instanceof ApiError ? error.message : t("forgotFailed"));
    }
  });

  return (
    <AuthBrandLayout title={t("forgotHeading")} subtitle={t("forgotTagline")}>
      {sent ? (
        <div className="flex flex-col gap-4 rounded-lg border border-border-subtle bg-surface p-6 shadow-sm">
          <Alert tone="info" title={t("forgotSentTitle")} announce={false}>
            {t("forgotSentBody")}
          </Alert>
          <Link to="/login" className="text-sm font-medium text-brand-strong hover:underline">
            {t("forgotBackToSignIn")}
          </Link>
        </div>
      ) : (
        <form
          onSubmit={(event) => void onSubmit(event)}
          noValidate
          className="flex flex-col gap-4 rounded-lg border border-border-subtle bg-surface p-6 shadow-sm"
        >
          {formError ? <Alert>{formError}</Alert> : null}

          <TextField
            label={t("loginEmail")}
            type="email"
            autoComplete="username"
            required
            {...(errors.email?.message === undefined ? {} : { error: errors.email.message })}
            {...register("email")}
          />

          <Button type="submit" loading={isSubmitting}>
            {t("forgotSubmit")}
          </Button>

          <Link to="/login" className="text-sm font-medium text-brand-strong hover:underline">
            {t("forgotBackToSignIn")}
          </Link>
        </form>
      )}
    </AuthBrandLayout>
  );
}
